"use client";
import { useState, useRef, useEffect, useCallback, Suspense, lazy } from "react";
import PropTypes from "prop-types";
import { Disclosure } from "@headlessui/react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

import classNames from "classnames";

import DownArrowIcon from "assets/svg/chevron-down.svg";

import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";

import {
  initialTradeTypes,
  initialPriceTypes,
  yearIndexEquals,
  tradeTypeEquals,
  graphicPriceYearTabs,
} from "mock/endeksa";

import { getEndeksaPriceSectionInfos, getEndeksaPriceSectionInfosForProject } from "services/endeksa";

import { numberFormatter } from "helpers/number-formatter";

import Loading from "components/common/loading-spinner";
import Unit from "@/src/utils/constants/units";

import styles from "./styles.module.css";

ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ...registerables);

const Dropdown = lazy(() => import("components/common/dropdown"));
const ArrowUpGreen = lazy(() => import("assets/svg/down_arrow_icon.svg"));
const Line = lazy(() => import("react-chartjs-2").then((module) => ({ default: module.Line })));

const PriceGraphic = ({
  districtReportPriceInfos,
  listingEstateTypeValue = "",
  listingTradeTypeValue = "",
  listingData,
  isProjectPage = false,
  unitTypes = [],
  isPriceGraphicMarkerVisible = true,
}) => {
  const { id, slug } = listingData || {};
  const [isOpenPriceGraphic, setIsOpenPriceGraphic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [priceSectionInfos, setPriceSectionInfos] = useState({ ...districtReportPriceInfos });
  const [activeYearIndex, setActiveYearIndex] = useState(0);
  const [priceType, setPriceType] = useState("PRICE");
  const [tradeType, setTradeType] = useState(tradeTypeEquals[listingTradeTypeValue]);
  const [unitType, setUnitType] = useState(unitTypes[0]?.roomCount);
  const [graphicData, setGraphicData] = useState(null);
  const yearTabsRef = useRef();
  const oneYearLaterIndex = 3;
  const [visibleUnitTypesDropdown, setVisibleUnitTypesDropdown] = useState(true);

  const {
    graphics: priceGraphics,
    graphicPrice,
    graphicDataExists,
    graphicSectionVisible,
    price,
  } = priceSectionInfos || {};
  // Finding the maximum point on the y-axis of the today line on the graph according to the incoming data
  const todayLineMinYAxis = Math.max(...((graphicData && graphicData?.datasets[0]?.data) || []));
  const todayLineMinAndMaxXAxis = graphicData?.listingPriceIndex ?? null;
  const isDisplayGraphicPrice = todayLineMinYAxis > graphicPrice;

  const exchangeStateControl = graphicData?.exchangeState;

  const exchangeRatioStyle = classNames({
    [styles.exchangeRatio]: true,
    [styles.decreaseTrend]: graphicData?.exchangeState !== "INCREASING_RATE",
  });

  const graphic = classNames({
    [styles.graphic]: true,
    [styles.blurEffect]: loading,
  });

  const contentClasses = classNames({
    [styles.exchangeContent]: true,
    [styles.decrasaeContent]: graphicData?.exchangeState !== "INCREASING_RATE",
  });

  const priceCircleIconClasses = classNames({
    [styles.projectPriceCircleIcon]: isProjectPage,
    [styles.listingPriceCircleIcon]: !isProjectPage,
  });

  const getEndeksaPriceSectionResponse = async (currentValues = {}) => {
    setLoading(true);
    const { tradeType, priceType } = currentValues;
    try {
      const response = await getEndeksaPriceSectionInfos(
        id,
        tradeType ?? tradeTypeEquals[listingTradeTypeValue],
        priceType
      );
      setPriceSectionInfos(response?.result);
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const getEndeksaPriceSectionResponseForProject = async (roomCount, priceType) => {
    setLoading(true);
    try {
      const response = await getEndeksaPriceSectionInfosForProject(slug, roomCount, priceType);
      setPriceSectionInfos(response?.result);
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const setActiveYearTab = (event) => {
    const yearParentRef = yearTabsRef?.current?.childNodes;
    yearParentRef.forEach((year, index) => {
      if (index === activeYearIndex) {
        year.removeAttribute("class");
        event.target.classList.add(styles.activeYear);
      }
    });
  };

  const handleChangeYear = (label, event) => {
    setActiveYearTab(event);
    setActiveYearIndex(yearIndexEquals[label]);
  };

  const checkAndSetActiveYearTabClass = (currentYearIndex = activeYearIndex) => {
    const yearParentRef = yearTabsRef?.current?.childNodes;
    if (yearParentRef) {
      yearParentRef.forEach((year, index) => {
        year.removeAttribute("class");
        if (index === currentYearIndex) {
          year.classList.add(styles.activeYear);
        }
      });
    }
  };

  // Why ?? When the chart is first opened or tradeType dropdown change, it is sorted according to the year filled in.
  const setInitialYearIndex = (currentTradeType = tradeType) => {
    let existedActiveYearIndex = 0;
    let isDesiredIndexFound = false;
    const indexedAndSortedGraphicDataExistList = graphicDataExists
      ?.filter((item) => item?.tradeType === currentTradeType)
      .map((item) => {
        item.index = yearIndexEquals[item.yearOption];
        return item;
      })
      .sort((a, b) => a.index - b.index);

    indexedAndSortedGraphicDataExistList?.forEach((item) => {
      if (item.exist && !isDesiredIndexFound) {
        existedActiveYearIndex = item.index;
        isDesiredIndexFound = true;
      }
    });
    setActiveYearIndex(existedActiveYearIndex);
    checkAndSetActiveYearTabClass(existedActiveYearIndex);
  };

  const handleDropdownChange = (value, type) => {
    let currentValues = {
      tradeType,
      priceType,
    };

    if (type === "tradeType") setTradeType(value?.key);
    else if (type === "priceType") setPriceType(value?.key);

    currentValues = {
      ...currentValues,
      [type]: value?.key,
    };
    getEndeksaPriceSectionResponse({ ...currentValues });
    setInitialYearIndex(value?.key);
  };

  const handleDropdownChangeForProject = (value, type) => {
    if (type === "unitType") {
      setUnitType(value?.roomCount);
      getEndeksaPriceSectionResponseForProject(value.roomCount, priceType);
    } else if (type === "priceType") {
      value.key === "m²" ? setVisibleUnitTypesDropdown(false) : setVisibleUnitTypesDropdown(true);
      const price = value.key === "m²" ? "M2" : value.key;
      setPriceType(price);
      getEndeksaPriceSectionResponseForProject(unitType, price);
    }
  };

  // Converting data from BE in a way that the graph will use schema
  const convertGraphicData = () => {
    if (priceGraphics?.length > 0) {
      return priceGraphics.map((data, index) => {
        const { monthNameList, monthDataList, ...otherProperties } = data || {};
        return {
          ...otherProperties,
          index,
          labels: data.monthNameList,
          datasets: [
            {
              type: "line",
              data: data.monthDataList,
              fill: true,
              backgroundColor: "rgba(229, 245, 226, 0.6)",
              borderColor: "#57b846",
              hoverBackgroundColor: "#57b846",
              hoverBorderColor: "white",
              hoverBorderWidth: 2,
              order: 2,
            },
          ],
        };
      });
    }
  };

  // Why ?? because: We need a schema chart to place the price of the ad within the chart.
  // Here, an array is created according to the number of elements of the total array
  const getListingPriceData = (totalArrayLength = 0, listingPriceIndex = 0, listingPriceValue = 0, isShowMarker) => {
    const listingPriceGraphArray = Array.from({ length: totalArrayLength });
    listingPriceGraphArray.forEach((_, index) => {
      if (index === listingPriceIndex && listingPriceValue) {
        listingPriceGraphArray[listingPriceIndex] = listingPriceValue;
      } else {
        listingPriceGraphArray[index] = null;
      }
    });

    if (isPriceGraphicMarkerVisible) {
      return {
        type: "scatter",
        label: "Bu İlanın Fiyatı",
        data: listingPriceGraphArray,
        radius: 7,
        backgroundColor: "#08bbb6",
        borderColor: "#08bbb6",
        pointStyle: "circle",
        hoverBorderWidth: 10,
        order: 1,
      };
    }
  };

  const setGraphicDataToGraphics = () => {
    if (priceGraphics?.length > 0) {
      const allDatasForYears = convertGraphicData();
      const currentPriceDatas = allDatasForYears.find((data) => data.index === activeYearIndex);
      const existedPriceDatas = graphicDataExists.find(
        (data) => data.yearOption === currentPriceDatas?.key && data?.tradeType === tradeType
      )?.exist;
      const { listingPriceIndex, datasets } = currentPriceDatas ?? {};
      const listingPriceGraphData = getListingPriceData(
        datasets?.length > 0 && datasets[0]?.data?.length,
        listingPriceIndex,
        isProjectPage && visibleUnitTypesDropdown ? graphicPrice || price : graphicPrice
      );
      // Mavi top
      if (listingPriceGraphData && isDisplayGraphicPrice) {
        currentPriceDatas.datasets.push(listingPriceGraphData);
      }
      currentPriceDatas.exist = existedPriceDatas;
      setGraphicData(currentPriceDatas);
    }
  };

  const handleOpenPriceGraphic = useCallback(() => {
    setIsOpenPriceGraphic(true);
    setTimeout(() => {
      Ga4WithEventParameters({
        eventName: "click_widget",
        eventParameters: { widget: "bolge-raporu||fiyat-trendi" },
      });
    }, 500);
  }, []);

  const options = {
    scales: {
      y: {
        stacked: true,
        grid: {
          circular: true,
          drawTicks: false,
        },
        ticks: {
          padding: 9,
          beginAtZero: true,
          // Adding a string value to the prices on the y-axis
          callback(value) {
            if (value === 0) return value;
            return `${numberFormatter(value)} ${priceType === "PRICE" ? "TL" : `TL/${Unit.M2.name}`}`;
          },
        },
      },
      x: {
        grid: {
          drawTicks: true,
        },
        ticks: {
          callback(val, index) {
            // Hide every 2nd and 3nd tick label
            // eslint-disable-next-line react/no-this-in-sfc
            const labelValue = this.getLabelForValue(val);
            const isHideEverySecondTickLabel = [0, 3].includes(activeYearIndex);
            const threeYearLaterIndex = 2;
            if (isHideEverySecondTickLabel) {
              return index % 2 === 0 ? labelValue : "";
            }
            if (activeYearIndex === threeYearLaterIndex) {
              return index % 4 === 0 ? labelValue : "";
            }
            return index % 3 === 0 ? labelValue : "";
          },
        },
      },
    },
    plugins: {
      // this annotation has been added to show the today line on the chart
      annotation: {
        annotations: [
          {
            display: graphicData?.exist,
            type: "line",
            borderColor: "#bcc2c7",
            borderDash: [4, 4],
            borderDashOffset: 0,
            borderWidth: 4,
            xMax: todayLineMinAndMaxXAxis,
            xMin: todayLineMinAndMaxXAxis,
            yMax: 0,
            yMin: null,
            drawTime: "beforeDatasetsDraw",
            label: {
              backgroundColor: "transparent",
              color: "#000",
              enabled: true,
              content: "Bugün",
              position: "start",
              padding: {
                left: -5,
                top: -14,
              },
            },
          },
        ],
      },
      legend: false,
      tooltip: {
        backgroundColor: "#fcfcfc",
        titleColor: "#2d393e",
        bodyColor: "#8f9fa9",
        borderWidth: 1,
        borderColor: "#bcc2c6",
        displayColors: false,
        callbacks: {
          // Adding a string value to the listing price value && endeksa value
          title: (data) => {
            if (data[0]?.dataset?.type !== "scatter") {
              return `${data[0].formattedValue} ${priceType === "PRICE" ? "TL" : `TL/${Unit.M2.name}`}`;
            }
            return null;
          },
          label: (labels) => {
            if (labels?.dataset?.type === "scatter") {
              return `${labels.dataset.label}: ${labels.formattedValue} TL`;
            }
            return labels.label;
          },
        },
      },
    },
  };

  useEffect(() => {
    checkAndSetActiveYearTabClass();
    setInitialYearIndex();
  }, [isOpenPriceGraphic]);

  // It includes the operations of arranging the data from the graphic as desired
  useEffect(() => {
    setGraphicDataToGraphics();
  }, [activeYearIndex, priceSectionInfos, isDisplayGraphicPrice]);

  const renderContent = () => {
    return (
      <>
        <div className={styles.priceGraphicInfoWrapper}>
          <div className={styles.dropdownWrapper}>
            {isProjectPage ? (
              <>
                <div className={styles.dropdowns}>
                  <Dropdown
                    options={initialPriceTypes}
                    selectedOption={initialPriceTypes.find((item) => item.key === priceType)}
                    onChange={(e) => handleDropdownChangeForProject(e, "priceType")}
                  />
                </div>
                {visibleUnitTypesDropdown && (
                  <div className={styles.dropdowns}>
                    <Dropdown
                      options={unitTypes}
                      selectedOption={unitTypes.find((item) => item.key === tradeType)}
                      onChange={(e) => handleDropdownChangeForProject(e, "unitType")}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                {listingEstateTypeValue !== "Arsa" && (
                  <div className={styles.dropdowns}>
                    <Suspense fallback={<Loading />}>
                      <Dropdown
                        options={initialTradeTypes}
                        selectedOption={initialTradeTypes.find((item) => item.key === tradeType)}
                        onChange={(e) => handleDropdownChange(e, "tradeType")}
                      />
                    </Suspense>
                  </div>
                )}

                <div className={styles.dropdowns}>
                  <Suspense fallback={<Loading />}>
                    <Dropdown
                      options={initialPriceTypes}
                      selectedOption={initialPriceTypes.find((item) => item.key === priceType)}
                      onChange={(e) => handleDropdownChange(e, "priceType")}
                    />
                  </Suspense>
                </div>
              </>
            )}
          </div>
          <div className={styles.exchange}>
            <span className={styles.exchangeInfo}>
              {activeYearIndex !== oneYearLaterIndex ? `${activeYearIndex + 1} Yıllık ` : "1 Yıl Sonraki "}
              Değer Değişimi
            </span>
            {graphicData?.exchangeRatio ? (
              <div className={contentClasses}>
                <p className={exchangeRatioStyle}>{`%${graphicData?.exchangeRatio}`}</p>
                {exchangeStateControl === "INCREASING_RATE" ? (
                  <div className={styles.iconArrow}>
                    <Suspense fallback={<Loading />}>
                      <ArrowUpGreen />
                    </Suspense>
                  </div>
                ) : (
                  <div className={styles.downArrow}>
                    <Suspense fallback={<Loading />}>
                      <ArrowUpGreen />
                    </Suspense>
                  </div>
                )}
              </div>
            ) : (
              <p className={styles.noExchangeInfo}>-</p>
            )}
          </div>
        </div>
        {graphicSectionVisible && (
          <div className={styles.graphicContainer}>
            {!graphicData?.exist && (
              <div className={styles.notExistedDataScreen}>
                <p className={styles.notExistedDataScreenInfo}>Yeterli Veri Bulunmamaktadır</p>
              </div>
            )}
            <div className={graphic}>
              {graphicData && (
                <Suspense fallback={<Loading />}>
                  <Line data={graphicData} options={options} />
                </Suspense>
              )}
              {loading && (
                <div className={styles.loadingWrapper}>
                  <Loading />
                </div>
              )}
            </div>
            <div className={styles.yearTabs} ref={yearTabsRef}>
              {graphicPriceYearTabs.map((tab, index) => (
                <span key={index} onClick={(e) => handleChangeYear(tab.key, e)}>
                  {tab.value}
                </span>
              ))}
            </div>
          </div>
        )}
        {graphicData && (
          <div className={styles.listingPrice}>
            {visibleUnitTypesDropdown && <div className={priceCircleIconClasses} />}
            <p className={styles.listingPriceValue}>
              {isProjectPage && visibleUnitTypesDropdown && price ? (
                <>
                  Bu ünite tipinin fiyatı:
                  <b>{price ? `${numberFormatter(price)} TL` : "-"}</b>
                </>
              ) : (
                <>
                  {graphicPrice && (
                    <>
                      Bu İlanın Fiyatı:
                      <b>{graphicPrice ? `${numberFormatter(graphicPrice)} TL` : "-"}</b>
                    </>
                  )}
                </>
              )}
            </p>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.disclosureWrapper}>
      {isProjectPage ? (
        renderContent()
      ) : (
        <Disclosure>
          {({ open }) => {
            return (
              <>
                <Disclosure.Button onClick={handleOpenPriceGraphic} className={styles.disclosureButton}>
                  <span>Bölgenin Fiyat Trendi</span>
                  <div className={`${open ? "rotate-180 transform" : ""}`}>
                    <DownArrowIcon />
                  </div>
                </Disclosure.Button>

                <Disclosure.Panel className={styles.disclosureContent}>{renderContent()}</Disclosure.Panel>
              </>
            );
          }}
        </Disclosure>
      )}
    </div>
  );
};

PriceGraphic.propTypes = {
  listingEstateTypeValue: PropTypes.string,
  listingTradeTypeValue: PropTypes.string,
  districtReportPriceInfos: PropTypes.object,
  listingData: PropTypes.object,
  isProjectPage: PropTypes.bool,
  unitTypes: PropTypes.array,
  isPriceGraphicMarkerVisible: PropTypes.bool,
};

export default PriceGraphic;
